import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

/**
 * Searchable select dropdown built on Command + Popover.
 * Props:
 *   options      – [{ value, label }]
 *   value        – current selected value
 *   onValueChange – (value) => void
 *   placeholder  – trigger placeholder text (default "Select...")
 *   className    – extra classes on the trigger button
 *   disabled     – disable the trigger
 *   allowClear   – show a clear option when a value is selected
 */
export function SearchableSelect({
  options = [],
  value,
  onValueChange,
  placeholder = "Select...",
  className,
  disabled = false,
  allowClear = false,
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-expanded={open}
          className={cn(
            "w-full flex items-center justify-between gap-2 px-3 py-2 text-sm",
            "bg-white border border-slate-300 rounded-md transition-all cursor-pointer",
            "focus:outline-none focus:ring-2 focus:ring-[#334E7B]/20 focus:border-[#334E7B]",
            "disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed",
            selected ? "text-slate-900" : "text-slate-400",
            className
          )}
        >
          <span className="truncate flex-1 text-left">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown size={14} className="shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[--radix-popover-trigger-width] min-w-[200px]"
        align="start"
        sideOffset={4}
      >
        <Command>
          <CommandInput placeholder="Search..." className="h-9 text-sm" />
          <CommandList>
            <CommandEmpty className="py-4 text-center text-sm text-slate-500">
              No results found.
            </CommandEmpty>
            <CommandGroup>
              {allowClear && value && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => { onValueChange(""); setOpen(false); }}
                  className="text-slate-400 italic"
                >
                  — Clear selection
                </CommandItem>
              )}
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.label}
                  onSelect={() => { onValueChange(o.value); setOpen(false); }}
                >
                  <Check
                    size={14}
                    className={cn("mr-2 shrink-0", value === o.value ? "opacity-100" : "opacity-0")}
                  />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
